import { useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import LANDING_MARKUP from './landing/markup';
import setupLandingAnimations from './landing/animations';
import './landing/qombra-landing.css';
import { ACCESS_MAILTO, TALK_MAILTO } from '../utils/contactMailto';

// Section deep links that are their own route (see App.js): path -> element id.
// A map rather than a set because the two are not always the same word — the
// QAgent benchmark card is #qa-bars in the markup but /qagent-benchmark in a URL,
// where it has to say which product it belongs to. Keeping the list explicit means an
// unknown path never silently scrolls somewhere arbitrary.
const SECTION_ROUTES = new Map([
  ['qbrain', 'qbrain'],
  ['qagent', 'qagent'],
  ['qagent-benchmark', 'qa-bars'],
]);

// The landing page is the finalized standalone design from qombra-landing-frontier.html.
// Its markup ships verbatim (dangerouslySetInnerHTML) so the design is pixel-exact;
// its scroll/animation behavior lives in setupLandingAnimations(); CTA routing and
// the footer year are wired here so they hook into the app's router instead of the
// source file's placeholder hrefs.
const LandingPage = () => {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const rootRef = useRef(null);

  useEffect(() => {
    const root = rootRef.current;
    if (!root) return undefined;

    // Footer year (the source had 2026 stamped in by hand).
    const yr = root.querySelector('#yr');
    if (yr) yr.textContent = String(new Date().getFullYear());

    // Route CTAs through the SPA router / mailto, and jump the in-page anchors.
    // The source relied on `html { scroll-behavior: smooth }` for its nav links,
    // which the generated stylesheet can no longer set (it is scoped to the
    // wrapper, and the wrapper is not the scroller), so the scroll happens here.
    // The sticky nav overlaps whatever a raw anchor lands on, hence the offset.
    const NAV_OFFSET = 78;

    // Document-relative top, walked up the offset chain rather than read from
    // getBoundingClientRect(): every section heading is a .reveal, and an
    // unrevealed one is still sitting 14px low under its opening transform.
    // offsetTop ignores transforms, so a link fired before the target has
    // revealed lands in the same place as one fired after.
    const documentTop = (el) => {
      let top = 0;
      for (let node = el; node; node = node.offsetParent) top += node.offsetTop;
      return top;
    };

    const scrollToId = (id, behavior) => {
      const target = document.getElementById(id);
      if (!target) return false;
      // Align on the section's HEADING, not its box. Each section carries top
      // padding as breathing room; landing on the box top parks that padding
      // under the nav and drops the heading well below it, which reads as
      // having overshot the section.
      const anchor = target.querySelector('.h2') || target;
      window.scrollTo({ top: Math.max(0, documentTop(anchor) - NAV_OFFSET), behavior });
      return true;
    };

    const onClick = (e) => {
      const a = e.target.closest('a');
      if (!a || !root.contains(a)) return;
      if (a.hasAttribute('data-login')) { e.preventDefault(); navigate('/login'); return; }
      if (a.hasAttribute('data-talk')) { e.preventDefault(); window.location.href = TALK_MAILTO; return; }
      if (a.hasAttribute('data-access')) { e.preventDefault(); window.location.href = ACCESS_MAILTO; return; }
      if (a.hasAttribute('data-route')) { e.preventDefault(); navigate(a.getAttribute('data-route')); return; }
      // The web-app/SDK switcher's CTA follows the selected tab (the animation
      // code relabels it): the app opens the web app, the SDK asks for a key.
      if (a.hasAttribute('data-ways-cta')) {
        e.preventDefault();
        const sdk = root.querySelector('#ways [data-way="sdk"][aria-selected="true"]');
        if (sdk) window.location.href = ACCESS_MAILTO; else navigate('/app');
        return;
      }
      const href = a.getAttribute('href') || '';
      if (!href.startsWith('#')) return;
      e.preventDefault();
      const id = href.slice(1);
      // The brand marks ("#") return to the absolute top.
      if (!id || id === 'top') { window.scrollTo({ top: 0, behavior: 'smooth' }); return; }
      scrollToId(id, 'smooth');
    };
    root.addEventListener('click', onClick);

    // Arriving from another page's footer (/#qbrain and friends): the markup
    // has only just been written into the DOM, so the section is not laid out
    // at its final offset yet — hence the frame's delay, and the jump rather
    // than a smooth glide from a top the reader never saw.
    // Two ways in: a hash (/#qbrain, from another page's footer) and a path
    // (/qbrain, its own route). The path wins only when there is no hash, so a
    // fragment on top of a section route still decides where the reader lands.
    const slug = pathname.replace(/^\/+|\/+$/g, '').toLowerCase();
    const hash = window.location.hash.slice(1) || SECTION_ROUTES.get(slug) || '';
    const hashJump = hash ? requestAnimationFrame(() => scrollToId(hash, 'auto')) : null;

    const teardownAnimations = setupLandingAnimations();
    document.body.classList.add('qombra-landing-active');

    return () => {
      root.removeEventListener('click', onClick);
      if (hashJump !== null) cancelAnimationFrame(hashJump);
      if (typeof teardownAnimations === 'function') teardownAnimations();
      document.body.classList.remove('qombra-landing-active');
    };
  }, [navigate, pathname]);

  return (
    // `js` is what the source stamped on <html> to guard its reveal/count-in
    // opening states, so that a page with no JS to un-hide them renders solid
    // instead of blank. Here it is static: the markup only exists once React ran.
    <div
      className="qombra-landing js"
      ref={rootRef}
      dangerouslySetInnerHTML={{ __html: LANDING_MARKUP }}
    />
  );
};

export default LandingPage;
